import { useEffect, useRef } from 'react'

const TYPING_SPEED = 30
const DELETING_SPEED = 30
const PAUSE_AFTER_TYPING = 2000
const PAUSE_AFTER_DELETING = 500

type TPhase = 'typing' | 'paused' | 'deleting' | 'waiting'

export const useTypingPlaceholder = (
  texts: string[],
  isActive: boolean,
  fallbackPlaceholder = ''
) => {
  const elementRef = useRef<HTMLTextAreaElement | null>(null)

  const textIndexRef = useRef(0)
  const charIndexRef = useRef(0)
  const phaseRef = useRef<TPhase>('typing')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!elementRef.current) return

    const tick = () => {
      const el = elementRef.current
      if (!el) return

      if (!isActive) {
        el.placeholder = fallbackPlaceholder
        textIndexRef.current = 0
        charIndexRef.current = 0
        phaseRef.current = 'typing'
        return
      }

      const currentText = texts[textIndexRef.current]
      const phase = phaseRef.current

      if (phase === 'typing') {
        charIndexRef.current++
        el.placeholder = currentText.slice(0, charIndexRef.current)

        if (charIndexRef.current >= currentText.length) {
          phaseRef.current = 'paused'
          timerRef.current = setTimeout(tick, PAUSE_AFTER_TYPING)
        } else {
          timerRef.current = setTimeout(tick, TYPING_SPEED)
        }
      } else if (phase === 'paused') {
        phaseRef.current = 'deleting'
        timerRef.current = setTimeout(tick, DELETING_SPEED)
      } else if (phase === 'deleting') {
        charIndexRef.current--
        el.placeholder = currentText.slice(0, charIndexRef.current)

        if (charIndexRef.current <= 0) {
          phaseRef.current = 'waiting'
          timerRef.current = setTimeout(tick, PAUSE_AFTER_DELETING)
        } else {
          timerRef.current = setTimeout(tick, DELETING_SPEED)
        }
      } else {
        textIndexRef.current = (textIndexRef.current + 1) % texts.length
        phaseRef.current = 'typing'
        timerRef.current = setTimeout(tick, TYPING_SPEED)
      }
    }

    timerRef.current = setTimeout(tick, TYPING_SPEED)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [texts, isActive, fallbackPlaceholder])

  return elementRef
}
